import { useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from "react-i18next";
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { Table, TableBody, TableHead, TableCell, TableContainer, TableRow, Chip, Box, Tab, Tabs } from '@mui/material';
import { Row, Col, NavLink } from 'react-bootstrap';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';

import { getDashboardStatsEquipmentAlertCountByPeriodType, getDashboardStatsEquipmentCount, getDashboardStatsEquipmentZoneStatus, getDashboardStatsMonthlyEquipmentAlertCount } from 'components/axios/dashboardAxios';
import { useEnumApi } from 'contexts/EnumApiContext';
import MainCard from 'components/MainCard';
import TrainMap from 'components/TrainMapNantong';
import TrainMap1 from 'components/TrainMapNantong1';
import TrainMap2 from 'components/TrainMapNantong2';
import StatusChart from 'components/inner/chart/StatusChart';
import PieChart from 'components/inner/chart/PieChart';
import { getEquipmentAlertColor, getEquipmentCtgrImageNameByCodeOfDashboardNantong } from 'data/data';
import DashboardGradientLineChart from 'components/inner/chart/DashboardGradientLineChart';
import AlertListCard from 'components/inner/mainCard/AlertListCard';
import { getEquipmentAlertsByQueryString } from 'components/axios/equipmentAlertsAxios';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';

// 플러그인 확장
dayjs.extend(utc);
dayjs.extend(timezone);

const DELAY_TIME = Number(process.env.REACT_APP_DELAY_TIME) || 60000;

function DashboardNantongMonitering() {
  const nantongCompanyId = process.env.REACT_APP_NANTONG_MANAGEMENT_COMPANY_NO;
  const { getEnumValueByEnumName, equipmentCategoryLevel1s, useNantongSubwayLines } = useEnumApi();
  const { t } = useTranslation();
  const { subwayLines, subwayLineByCode } = useNantongSubwayLines();

  const { loginId } = useSelector(state => state.user);
  const isLoggedIn = loginId !== 'NONE';

  const EquipmentFaultStatusList = useMemo(() => {
    return (
      getEnumValueByEnumName("EquipmentFaultStatus")
        ?.map((efs) => ({
          ...efs,
          code2: `count${efs.code.replace('_', '')}`
        })) ?? []
    );
  }, [getEnumValueByEnumName]);

  const [swTab, setSwTab] = useState('total');
  const [subwayline, setSubwayline] = useState('');
  const [subwaylineName, setSubwaylineName] = useState('');
  const [pdTab, setPdTab] = useState("YEAR");

   // ===========================
  // tab select
  // ===========================
  const selectSubwayTab = (
    event,
    tabCode
  ) => {
    setSwTab(tabCode);
    if (tabCode === 'total') {
      setSubwayline('');
      setSubwaylineName('');
    } else {
      setSubwayline(tabCode);
      const subway =
        subwayLineByCode(tabCode);
      setSubwaylineName(
        subway?.title ?? ''
      );
    }
  };

  // ===========================
  // subwayline count
  // ===========================
  const subwaylineCount = useMemo(() => {
    if (swTab !== 'total') {
      return 1;
    }
    return subwayLines?.length ?? 0;
  }, [subwayLines, swTab]);

  // ===========================
  // query
  // ===========================
  const {
    data,
    isLoading,
    isFetching
  } = useQuery({
    queryKey: [
      'nantong-dashboard',
      swTab, pdTab
    ],
    enabled:
      isLoggedIn &&
      equipmentCategoryLevel1s?.length > 0,

    refetchInterval: DELAY_TIME,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // 🔥 탭 안 보여도 멈추지 않음(기본값은 false)
    refetchIntervalInBackground: true,
    
    staleTime: DELAY_TIME,
    placeholderData: (prev) => prev,

    queryFn: async () => {
      const equipmentParam = {
        iotInstlYn: true,
      };
      let alertParam = {
        page: 0,
        size: 5,
        "equipment.iotInstlYn": true,
        "equipment.opStatus" : "O"
      };
      let zoneParam = {...equipmentParam};
      if (swTab !== 'total') {
        alertParam[
          "equipment.zone.category.code"
        ] = swTab;
        zoneParam["zoneCategoryCode"] = swTab;
      }
      let addQueryString =
        "&sort=status,asc&sort=alertedAt,desc";
      let queryStringValue = Object.entries(alertParam).map(([key, value]) => {
          if (value === undefined ||value === null) {
            return null;
          }
          return `${key}=${value}`;
        })
        .filter(Boolean)
        .join('&');
      queryStringValue += addQueryString;

      const [
        equipmentCounts,
        equipmentAlerts,
        zoneStatus,
        monthlyDatas
      ] = await Promise.all([
        getDashboardStatsEquipmentCount(equipmentParam),
        getEquipmentAlertsByQueryString("?" + queryStringValue),
        getDashboardStatsEquipmentZoneStatus(),
        getDashboardStatsEquipmentAlertCountByPeriodType(pdTab, zoneParam)
      ]);

      return {
        equipmentCounts,
        equipmentAlerts,
        zoneStatus,
        monthlyDatas
      };
    }
  });

  // ===========================
  // raw data
  // ===========================
  const equipmentCounts = data?.equipmentCounts;
  const equipmentAlerts = data?.equipmentAlerts;
  const zoneStatus      = data?.zoneStatus;
  const monthlyDatas    = data?.monthlyDatas;

  // ===========================
  // target data
  // ===========================
  const zoneCategoryByEquipment = useMemo(() => {
    if (swTab === 'total') {
      return null;
    }
    return equipmentCounts?.zoneCategories?.find( zoneCate => zoneCate.code === swTab );
  }, [equipmentCounts, swTab]);

  const zoneCategoryByZone = useMemo(() => {
    if (swTab === 'total') {
      return null;
    }
    return zoneStatus?.zoneCategories?.find( zoneCate => zoneCate.code === swTab );
  }, [zoneStatus, swTab]);

  // ===========================
  // alert datas
  // ===========================
  const alertDatas = useMemo(() => {
    return equipmentAlerts ?? null;
  }, [equipmentAlerts]);

  // ===========================
  // zone list
  // ===========================
  const zoneList = useMemo(() => {
    let zoneCategories;
    let companyCount;
    if (swTab === 'total') {
      zoneCategories = equipmentCounts?.zoneCategories;
      if (!zoneCategories?.length) {
        return [];
      }
      companyCount =
        zoneCategories
        ?.flatMap(line => line.managementCompanies)
        ?.find(mc => mc.id === parseInt(nantongCompanyId));
    } else {
      if (!zoneCategoryByEquipment) {
        return [];
      }
      companyCount =
        zoneCategoryByEquipment?.managementCompanies?.find(com=>com.id === parseInt(nantongCompanyId));
    }
    return (
      companyCount?.zones?.map(z => ({
        code: `${z.code}`,

        statusColor:
          getEquipmentAlertColor(
            z.equipmentCount.countO1N,
            z.equipmentCount.countO2I,
            z.equipmentCount.countO3A,
            z.equipmentCount.countO4C
          )
      })) ?? []
    );

  }, [
    swTab,
    equipmentCounts,
    zoneCategoryByEquipment,
    nantongCompanyId
  ]);

  // ===========================
  // station count
  // ===========================
  const stationCount = useMemo(() => {
    if (swTab === 'total') {
      return (
        zoneStatus?.zoneCount?.totalZoneCount ?? 0
      );
    }
    return (
      zoneCategoryByZone?.zoneCount?.totalZoneCount ?? 0
    );
  }, [
    swTab,
    zoneStatus,
    zoneCategoryByZone
  ]);

  // ===========================
  // equipment count
  // ===========================
  const equipmentIotCount = useMemo(() => {
    if (swTab === 'total') {
      return (
        equipmentCounts?.equipmentCount?.total ?? 0
      );
    }
    return (
      zoneCategoryByEquipment?.equipmentCount?.total ?? 0
    );

  }, [
    swTab,
    equipmentCounts,
    zoneCategoryByEquipment
  ]);

  // ===========================
  // status datas
  // ===========================
  const statusDatas = useMemo(() => {
    let target;
    if (swTab === 'total') {
      target = equipmentCounts?.equipmentCount;
    } else {
      target = zoneCategoryByEquipment?.equipmentCount;
    }

    return (
      EquipmentFaultStatusList
        ?.map(element => ({
          groups: [element.code],
          count:
            target?.[element.code2] ?? 0
        })) ?? []
    );

  }, [
    swTab,
    equipmentCounts,
    zoneCategoryByEquipment,
    EquipmentFaultStatusList
  ]);

  // ===========================
  // equipment category result
  // ===========================
  const equipmentCtgrResult = useMemo(() => {
    let topCategories;
    if (swTab === 'total') {
      topCategories = equipmentCounts?.equipmentTopCategories;
    } else {
      topCategories = zoneCategoryByEquipment?.equipmentTopCategories;
    }

    return (
      equipmentCategoryLevel1s
        ?.map((lvl1) => {
          const topCategory = topCategories?.find( etc => etc.code === lvl1.code );
          return {
            code: lvl1.code,
            count:
              topCategory?.equipmentCount?.total ?? 0,
            data:
              EquipmentFaultStatusList
                ?.map(element => ({
                  name: element.code,
                  count:topCategory?.equipmentCount?.[element.code2] ?? 0
                }))
          };
        }) ?? []
    );

  }, [
    swTab,
    equipmentCounts,
    zoneCategoryByEquipment,
    equipmentCategoryLevel1s,
    EquipmentFaultStatusList
  ]);

  function formatDynamicDate(dateStr) {
    if (!dateStr) return '';

    const parts = dateStr.split('-');

    // YYYY-MM-DD 인 경우 -> '일' (D: 1, DD: 01)
    if (parts.length === 3) {
      if (dateStr.indexOf(' ') != -1) {
        return dayjs(dateStr).format('HH');
      } else {
        return dayjs(dateStr).format('DD');
      }
    }

    // YYYY-MM 인 경우 -> '월' (M: 7, MM: 07)
    if (parts.length === 2) {
      return dayjs(dateStr).format('MM');
    }

    return dateStr;
  }

  // ===========================
  // bar data
  // ===========================
  const barData = useMemo(() => {
    if ( !monthlyDatas || !equipmentCategoryLevel1s ) {
      return null;
    }

    const alertBarCategories  = monthlyDatas?.timeSlotCounts?.map( m => formatDynamicDate(m.timeKey) );
    const alertBarLegends     = equipmentCategoryLevel1s?.map( lvl1 => lvl1.name );
    const alertBarCategories2 = monthlyDatas?.timeSlotCounts?.map( m => m.timeKey );
    
    const alertBarSerieDatas =
      equipmentCategoryLevel1s?.map(
        (lvl1) => {

          const datas =
            alertBarCategories2.map(
              (month) => {
                const monthlyData =
                  monthlyDatas
                    ?.timeSlotCounts
                    ?.find(
                      d =>
                        d.timeKey === month
                        // d.year ==
                        //   month.split('_')[0] &&
                        // d.month ==
                        //   parseInt(
                        //     month.split('_')[1]
                        //   )
                    );

                let dataMonth;
                if (swTab === 'total') {
                  dataMonth = monthlyData?.equipmentTopCategories?.find( m => m.code === lvl1.code );
                } else {
                  const monthlyData2 = monthlyData?.zoneCategories?.find( z => z.code === swTab );
                  dataMonth = monthlyData2?.equipmentTopCategories?.find( m => m.code === lvl1.code );
                }

                return {
                  value:
                    dataMonth?.count ?? 0,
                  link:
                    `/equipment-alert-history-tracking?searchlevel1=${lvl1.id}&searchSubway=${subwayline}`
                };
              }
            );

          return {
            name: lvl1.name,
            data: datas,
          };
        }
      );

    return {
      legends: alertBarLegends,
      categories: alertBarCategories,
      series: alertBarSerieDatas
    };

  }, [
    monthlyDatas,
    equipmentCategoryLevel1s,
    swTab,
    pdTab,
    subwayline,
    t
  ]);

  // ===========================
  // pie datas
  // ===========================
  // const pieDatas = useMemo(() => {
  //   return (
  //     equipmentCategoryLevel1s
  //       ?.map(lvl1 => {
  //         let count = 0;
  //         if (swTab === 'total') {
  //           count = monthlyDatas?.equipmentTopCategories?.find( d => d.code === lvl1.code )?.count ?? 0;
  //         } else {
  //           const alertCount = monthlyDatas?.zoneCategories?.find( com => com.code === swTab );
  //           count = alertCount?.equipmentTopCategories?.find( d => d.code === lvl1.code )?.count ?? 0;
  //         }

  //         return {
  //           title: lvl1.name,
  //           cnt: count,
  //           link:
  //             `/equipment-alert-history-tracking?searchlevel1=${lvl1.id}&searchSubway=${subwayline}`
  //         };
  //       }) ?? []
  //   );

  // }, [
  //   monthlyDatas,
  //   equipmentCategoryLevel1s,
  //   swTab,
  //   subwayline
  // ]);

  // ===========================
  // loading
  // ===========================
  // const isDataLoaded = !!data;

  let main_container_css = 'main_container1_nt';

  return (
      <div className={`main_container main_container1 ${main_container_css} cn_main_container`} style={{marginTop: "10px"}}>

        <Row className="g-3">
          <div className='cn_reg'>
            <Col sm={8} className="main_right">
              {/* 노선도 */}
              <Box sx={{ width: '100%', typography: 'body1' }} className="maintabs graCard">
                <TabContext value={swTab}>
                  <Box className="maintabspo">
                    <TabList onChange={selectSubwayTab} aria-label="lab API tabs example">
                      <Tab key="tabsall" className="tabsall" label={t('message.common.total')} value="total" />
                      {subwayLines && subwayLines.map((swl, idx)=>
                      <Tab key={`tabsline${(idx+1)}`} className={`tabsline${(idx+1)}`} label={swl.title} value={swl.code} />
                      )}
                    </TabList>
                  </Box>
                  <TabPanel value="total" style={{display:"flex"}}>
                    <div className='cn_dash_tabbox'>
                      <div className="map_box">
                        <TrainMap zoneList={zoneList} />
                      </div>
                    </div>
                  </TabPanel>
                  {subwayLines.map((el)=>{
                    return(
                  <TabPanel key={el.code} value={el.code} style={{display:"flex"}}>
                    <div className='cn_dash_tabbox'>
                      <div className="map_box">
                        {el.code === 'NT_L1' ?
                          <TrainMap1 zoneList={zoneList} />
                          :
                          <TrainMap2 zoneList={zoneList} />
                        }
                      </div>
                    </div>
                  </TabPanel>
                    )
                  })}
                </TabContext>
              </Box>

              {/* 대분류별 설비상태건수 */}
              <MainCard content={false} className="cn_status_box" style={{position:"relative", boxSizing: "border-box", marginTop: "3px", width:"100%"}}>
                <div className="small_motion_box main_small_motion_box" style={{justifyContent : "space-between"}}>
                  {equipmentCategoryLevel1s && equipmentCtgrResult && equipmentCategoryLevel1s?.map((lvl1, l)=> {
                    // console.log(equipmentCtgrResult)
                    const categoryData = equipmentCtgrResult && equipmentCtgrResult.find(ctgr => ctgr.code === lvl1.code);
                    // console.log(categoryData)
                    let normalCnt = 0, alertCnt = 0, referCnt = 0;
                    if (categoryData) {
                      normalCnt = categoryData?.data?.reduce((acc, cur)=>{
                        if (cur.name === 'O1_N') {
                            return acc + cur.count;
                        }
                        return acc
                      },0);

                      alertCnt = categoryData?.data?.reduce((acc, cur)=>{
                        if (cur.name === 'O3_A') {
                            return acc + cur.count;
                        }
                        return acc
                      },0);

                      referCnt = categoryData?.data?.reduce((acc, cur)=>{
                        if (cur.name === 'O2_I') {
                            return acc + cur.count;
                        }
                        return acc
                      },0);
                    }
                    return (
                  <div key={l} className="small_motion_group small_motion_group_nt">
                    <div className="small_motion">
                      <div>
                        <div className='bgwhite'>
                          <NavLink href={'/equipment?searchlevel1='+lvl1.id+'&searchSubway='+subwayline}>
                          {/* <video src={`${process.env.PUBLIC_URL}/movie/${getEquipmentCtgrImageNameByCodeOfDashboardNantong(lvl1.code)}`} className="detailEQMovie detailEQMovie_nantong_main" preload='auto' loop></video> */}
                          <img src={`${process.env.PUBLIC_URL}/img/small_motion/${getEquipmentCtgrImageNameByCodeOfDashboardNantong(lvl1.code)}`} alt='' />
                          </NavLink>
                        </div>

                        <TableContainer className="table_part">
                          <Table aria-label="simple table">
                            <TableBody>
                              <TableRow>
                                <TableCell>{t('message.common.total')}</TableCell>
                                <TableCell>{categoryData?.count ?? 0}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>{t('message.common.normal')}</TableCell>
                                <TableCell className='font_green'>{normalCnt}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>{t('message.common.interest')}</TableCell>
                                <TableCell className='font_yellow'>{referCnt}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>{t('message.common.title.alarm')}</TableCell>
                                <TableCell className='font_orange'>{alertCnt}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>      
                      </div>
                    </div>
                  </div>
                    )
                  })}
                </div>
              </MainCard>
            </Col>

            <Col sm={4}>
              <div className="mainTable" style={{display:"flex", justifyContent:"space-between"}}>
                {/* 등록 현황 */}
                <MainCard content={false} title="" className="graCard graCard1 dashLayout1">
                  <div style={{display: "flex", alignItems: "center"}}>
                    <div style={{padding:"5px 10px"}}>
                      <span className='bullet'><img src={`${process.env.PUBLIC_URL}/img/icon/dashboard.png`} alt='' /></span>&nbsp;
                      <Chip label={t('message.common.chip.registration.status')+(swTab === 'total' ? '' : `- ${subwaylineName}`)} color="default" />
                    </div>
                  </div>
                  
                  <TableContainer className="table_part">
                    <Table aria-label="simple table">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('message.common.subtitle.subwayline')}</TableCell>
                          <TableCell>{t('message.common.subtitle.station')}</TableCell>
                          <TableCell>{t('message.common.btn.equipment')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>{subwaylineCount}</TableCell>
                          <TableCell>{stationCount}</TableCell>
                          <TableCell>{equipmentIotCount}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>                          
                </MainCard>

                {/* 상태현황 */}
                <MainCard content={false} className="graCard graCard1 dashLayout2">
                    <div style={{display: "flex", alignItems: "center"}}>
                      <div style={{padding:"5px 10px"}}>
                        <span className='bullet'><img src={`${process.env.PUBLIC_URL}/img/icon/dashboard.png`} alt='' /></span>&nbsp;
                        <Chip label={t('message.common.chip.situation.status')+(swTab === 'total' ? '' : `- ${subwaylineName}`)} color="primary" />
                      </div>
                    </div>

                    <div className="charts_box2">
                      {(EquipmentFaultStatusList && statusDatas) && <StatusChart EquipmentFaultStatusList={EquipmentFaultStatusList} statusDatas={statusDatas} subwayCode={subwayline} />}
                    </div>
                </MainCard>
              </div>

              {/* 알림내역 */}
              <MainCard content={false} title="" className="graCard graCard1" style={{marginTop: "15px"}}>
                <div style={{display: "flex", alignItems: "center"}}>
                  <div style={{padding:"5px 10px"}}>
                    <span className='bullet'><img src={`${process.env.PUBLIC_URL}/img/icon/dashboard.png`} alt='' /></span>&nbsp;
                    <Chip label={t('message.common.chip.dashboard.alert.list')+(swTab === 'total' ? '' : `- ${subwaylineName}`)} color="primary" />
                  </div>
                </div>
                
                <div className="charts_box2">
                  <AlertListCard equipmentAlerts={alertDatas?._embedded?.equipmentAlerts} target='md' height="176px" />
                </div>
              </MainCard>

              <div className='graCard' style={{marginTop: "15px"}}>
                <MainCard content={false} className="cn_status_box" style={{position:"relative", boxSizing: "border-box", marginTop: "10px", width:"100%"}}>
                    <div style={{display: "flex", alignItems: "center", justifyContent:"space-between"}}>
                      <div style={{padding:"5px 10px"}}>
                        <span className='bullet'><img src={`${process.env.PUBLIC_URL}/img/icon/dashboard.png`} alt='' /></span>&nbsp;
                        <Chip label={t('message.common.chip.alert.status')+
                          (swTab === 'total' ? '' : `- ${subwaylineName}`)
                          } color="primary" />
                      </div>

                      <Tabs value={pdTab} onChange={(e, v) => setPdTab(v)} sx={{alignItems : "center", ".MuiTabs-flexContainer" : {justifyContent:"center"}}}>
                          <Tab label={t("message.common.text.daily")} value="DAY" className="graphTab"
                              style={{border:`${pdTab === "DAY" ? "3px solid #0061d0": "unset"}`}} />
                          <Tab label={t("message.common.text.monthly")} value="MONTH" className="graphTab"
                              style={{border:`${pdTab === "MONTH" ? "3px solid #0061d0": "unset"}`}} />
                          <Tab label={t("message.common.text.years")} value="YEAR" className="graphTab"
                              style={{border:`${pdTab === "YEAR" ? "3px solid #0061d0": "unset"}`}} />
                      </Tabs>

                    </div>
                    
                    <div className="charts_box2 mainChart">
                      {/* 경보현황 - 월별 선그래프 */}
                      {barData && barData.series?.length > 0 && <DashboardGradientLineChart key={JSON.stringify(barData)} barData={barData} height='240px' />}
                      {/* 경보현황 - 파이그래프 */}
                      {/* {pieDatas && <PieChart key={JSON.stringify(pieDatas)} pieDatas={pieDatas} subwayCode={subwayline} legendPosition="right" />} */}
                    </div>
                </MainCard>
              </div>
            </Col>
          </div>
        </Row>

        {/* <div className='footer'>
          <img src={`${process.env.PUBLIC_URL}/yihai.svg`} alt='' />
        </div> */}
      </div>
    )
  // }
}

export default DashboardNantongMonitering;
